<template>
    <div class="ss">
        <div v-if="shippingAddress.ZipCode" class="ss__container ss__container--shipping">
            <h2 class="ss__title" v-text="$ml.get('deliveryAddress')" tabindex="0" />
            <p class="ss__text ss__text--shipping" tabindex="0">
                <span>{{ shippingAddress.ZipCode }} - </span>
                <span>{{ shippingAddress.Street }}, </span>
                <span>{{ shippingAddress.Number }} </span>
                <span>{{ shippingAddress.District }} </span>
                <span>{{ shippingAddress.City }} - </span>
                <span>{{ shippingAddress.State }} </span>
                <span v-if="shippingAddress.Complement">- {{ shippingAddress.Complement }}</span>
            </p>
        </div>

        <div class="ss__container ss__container--cart">
            <h2 class="ss__title" v-text="$ml.get('paymentSummary')" tabindex="0" />

            <div class="cart-item" :key="key" v-for="(item, key) in cartItems" tabindex="0">
                <span :title="item.name" class="cart-item__name">({{ item.quantity }}) {{ item.name }}</span>
                <span class="cart-item__price">{{ formatPrice(item.unitPrice * item.quantity) }}</span>
            </div>

            <button
                v-if="hiddenItems > 0 & !shouldShowAllItems"
                @click="showAllItems"
                class="show-all"
                :aria-label="$ml.get('ariaSeeAllItems')"
            >
                <span v-text="shouldShowAllItems ? $ml.get('hideItems') : $ml.get('seeAllItems')" />
                <img
                    :class="{ active: shouldShowAllItems }"
                    src="@/assets/img/icons/arrow-down-blue.svg"
                    alt=""
                    tabindex="-1"
                />
            </button>

            <p class="ss__text-row ss__method" v-if="method.Type !== 'WithoutShipping'">
                <span> {{ method.Description }} </span>
                <span v-if="method.Price != 0">{{ formatPrice(method.Price) }}</span>
                <span v-if="method.Price == 0" class="free" v-text="$ml.get('free')" />
            </p>

            <p class="ss__text-row ss__discount" v-if="cartDiscount.DiscountPrice">
                <!-- <span>{{ $ml.get('cartDiscount') + ' : ' + cartDiscount.DiscountPercentage + '%' }}</span> -->
                <span v-text="$ml.get('cartDiscount')" />
                <span class="price">{{ `- ` + formatPrice(cartDiscount.DiscountPrice) }}</span>
            </p>

            <p class="ss__text-row ss__convenience-fee" v-if="convenienceFee">
                <span>
                    {{ $ml.get('convenienceFee') }}
                    <InfoTooltip
                        :items="['convenienceFeeInfoTooltip']"
                        :icon-color="'gray'"
                        :orientation="'top'"
                    />
                </span>
                <span class="price">{{ formatPrice(convenienceFee) }}</span>
            </p>

            <strong class="ss__text-row ss__total-price">
                <span v-text="$ml.get('totalAmount')" />
                <span v-text="totalText" />
            </strong>
        </div>


        <div v-if="productDescription" class="ss__container ss__container--description">
            <CartItemDescription
                :item-description="productDescription"
                :is-short-description="false"
                custom-class="order-status"
            />
        </div>

        <div 
            v-if="showReceiptActions"
            class="ss__container ss__container--receipt"
            data-html2canvas-ignore="true"
        >
            <CieloButton
                class="download__cta"
                :class="{ 'is-disabled': isReceiptActionDisabled }"
                :loading="receiptActionIsLoading"
                :transparent="true"
                @click="onDownloadReceipt"
            >
                <img src="@/assets/img/download-icon.svg" alt="Baixar" class="download-btn__icon" />
            </CieloButton>
            <CieloButton
                class="share__cta"
                :class="{ 'is-disabled': isReceiptActionDisabled }"
                :loading="receiptActionIsLoading"
                @click="onShareReceipt"
            >
                <img src="@/assets/img/share-icon.svg" alt="Compartilhar" width="24" height="24" class="share__icon" />
                <span>Compartilhar</span>
            </CieloButton>
        </div>
    </div>
</template>

<script>
	import { mapGetters } from 'vuex';
	import { logEvent } from '@/helpers/cieloLogger';
	import moneyHelper from '@/helpers/moneyHelper';
	import { formatConvertedAmount } from '@/helpers/dccCurrencyHelper';
	import receiptCaptureService from '@/helpers/receiptCaptureService';

	import InfoTooltip from '@/components/InfoTooltip';
	import CartItemDescription from '@/components/CartItemDescription';

	const CieloButton = () =>
		import(
			/* webpackChunkName: "cielo-button" */
			'@/components/template/CieloButton'
		);

	export default {
		data() {
			return {
				shouldShowAllItems: true,
				itemsToPreview: 2,
				receiptActionIsLoading: false
			};
		},

		props: {
			status: {
				type: String,
				required: true
			}
		},

		components: {
			CartItemDescription,
			CieloButton,
			InfoTooltip
		},

		computed: {
			...mapGetters({
				shippingAddress: 'orderStatus/getShippingAddress',
				productDescription: 'orderStatus/getFirstItemDescription',
				shoppingCartItems: 'orderStatus/getShoppingCartItems',
				method: 'orderStatus/getShippingMethod',
				totalPrice: 'orderStatus/getTotalPrice',
				cartDiscount: 'orderStatus/getCartDiscount',
				isBBPayPayment: 'bBPayPayment/getIsBBPayPayment',
				orderId: 'orderStatus/getOrderId',
				convenienceFee: 'orderStatus/getConvenienceFeeAmount',
				dcc: 'orderStatus/getDcc',
			}),

			/**
			 * Pedido com DCC mostra o total na moeda da cotação; os itens, o frete e o
			 * desconto acima continuam em reais.
			 */
			totalText() {
				return this.dcc
					? formatConvertedAmount(this.dcc)
					: this.formatPrice(this.totalPrice);
			},

			cartItems() {
				if (this.shouldShowAllItems)
					return this.shoppingCartItems;

				return this.shoppingCartItems.slice(0, this.itemsToPreview);
			},

			hiddenItems() {
				let hiddenItems = this.shoppingCartItems.length - this.itemsToPreview;

				return (hiddenItems > 0) ? hiddenItems : 0;
			},

			showReceiptActions() {
				return this.status === 'paid' || this.status === 'pending';
			},

			isReceiptActionDisabled() {
				return this.receiptActionIsLoading;
			}
		},

		methods: {
			showAllItems() {
				this.shouldShowAllItems = !this.shouldShowAllItems;
			},

			formatPrice(unitPrice) {
				let totalPrice = unitPrice / 100; // Transforma centavos em real.

				return `R$ ${moneyHelper.formatToPrice(totalPrice)}`;
			},

			async onDownloadReceipt() {
				if (this.isReceiptActionDisabled) {
					return;
				}

				this.receiptActionIsLoading = true;

				try {
					await receiptCaptureService.downloadPng({
						orderId: this.orderId
					});

					logEvent({
						category: 'checkout',
						action: 'baixar_comprovante',
						label: 'clique no botão de baixar comprovante.',
					});
				} catch (error) {
					console.error('Erro ao baixar o comprovante:', error);
				} finally {
					this.receiptActionIsLoading = false;
				}
			},

			async onShareReceipt() {
				if (this.isReceiptActionDisabled) {
					return;
				}

				this.receiptActionIsLoading = true;

				try {
					await receiptCaptureService.sharePdf({
						orderId: this.orderId,
						title: 'Comprovante de pagamento'
					});

					logEvent({
						category: 'checkout',
						action: 'compartilhar_comprovante',
						label: 'clique no botão de compartilhar comprovante.',
					});
				} catch (error) {
					console.error('Erro ao compartilhar o comprovante:', error);
				} finally {
					this.receiptActionIsLoading = false;
				}
			},
		},

	};
</script>

<style lang="scss" scoped>
.ss {
	margin-bottom: 32px;

	&__container {
		&:not(:last-child) {
			margin-bottom: 24px;
		}

		&--cart {
			.cart-item {
				display: flex;
				justify-content: space-between;
				font-size: 16px;
				font-weight: 500;
				color: #5C6A7B;

				&:not(:last-of-type) {
					margin-bottom: 8px;
				}
			}

			.show-all {
				//btn reset
				background: none;
				border: none;
				margin: 16px 0 0;
				padding: 0;
				cursor: pointer;

				span {
					font-size: 16px;
					font-weight: 700;
					color: #0667CD;
					text-decoration: underline;
				}

				img {
					margin-left: 8px;

					&.active {
						transform: rotate(180deg);
					}
				}
			}

			.ss__title {
				margin-bottom: 8px;
			}
		}

		&--description {}
	}

	&__title {
		font-size: 16px;
		font-weight: 700;
		color: #2F363E;
		margin-bottom: 8px;
	}

	&__text {
		font-size: 16px;
		font-weight: 500;
		color: #5C6A7B;
		margin: 0;
		overflow-wrap: break-word;
		word-wrap: break-word;
	}

	&__text-row {
		display: flex;
		justify-content: space-between;
		font-size: 16px;
		font-weight: 500;
		color: #5C6A7B;
		margin: 8px 0 0;
	}

	&__discount {
		.price {
			color: #00855D;
		}
	}

	&__total-price {
		color: #2F363E;
		font-weight: 700;
	}

	&__convenience-fee {
		span {
			display: flex;
			align-items: center;

			.it {
				margin-left: 8px;
			}
		}
	}

	&__container--receipt {
		display: flex;
		gap: 8px;

		.is-disabled {
			cursor: default;
		}

		.download__cta {
			min-width: initial;
			width: 44px;
			height: 44px;
			display: flex;
			justify-content: center;
			align-items: center;
			border-radius: 50%;
			padding: 6px;
		}

		.share__cta {
			min-width: initial;
			flex: 1;
		}
	}

}
</style>
